'use client';

import { FC, useState, useEffect, useCallback, useRef } from 'react';
import {
  CatTable,
  CatTableHeader,
  CatTableHeaderCell,
  CatTableRow,
  CatTableCell,
  CatTableBody,
  CatTextLink,
  CatTableObject,
  CatButton
} from 'components/blocks-components';
import { PurifiedHtml } from 'components/purified-html';
import { useTranslations } from 'next-intl';
import {
  INITIAL_REMAN_PARTS,
  MAX_REMAN_PARTS,
  PCC_PDP_REMAN_ADD_TO_CART
} from '../../constants/constants';
import Price from 'components/price';
import CommonAvailabilityWrapper from '../common-availability-wrapper';
import { DealerPriceAvailabilityResponse } from 'services/dealerPriceAvailability/types';
import PdpPartsMobileView from '../pdp-parts-mobile-view';
import { Conditional, useBreakpoint } from '@cat-ecom/pcc-components';
import { PAGE_BREAKPOINTS } from 'constants/common';
import { mergePriceAvailabilityResponse } from '../../utils/mergePriceAvailabilityRes';
import AddToCart from 'components/actions/add-to-cart/add-to-cart';
import cx from 'classnames';
import { useProductDetailPageContext } from './../../../[product]/context/product-detail';
import { useViewItemListEvent } from 'hooks/analytics/use-view-item-list-event';
import { ProductsPriceData } from 'app/[lang]/[esite]/(core)/(search-display)/_components/analytics-listener/types';
import { getFormattedSearchSelectItem } from 'components/product-comparison-grid/product-comparison-card/utils';
import { useSelectItemEvent } from 'hooks/analytics/use-select-item-event';
import {
  REMAN_SELECT_ITEM,
  REMAN_VIEW_ITEM_LIST
} from '../../constants/analytics-constants';
import ContactYourDealerPopover from 'components/contact-your-dealer/contact-your-dealer-popover/contact-your-dealer-popover';
import styles from './reman-parts.module.scss';

interface RemanPartsTableProps {
  remanPartsList: Product[];
  priceAvailabilityData: DealerPriceAvailabilityResponse;
  locale: string;
  dealerData;
  dealerInformation;
  availRemoveInterimStockFlag: boolean;
  isShowSourcingDetailEnabled: boolean;
  isDeliveryEnabled: boolean;
  catcorp: boolean;
  isPccReleaseRemanReplacement?: boolean;
  isRTL?: boolean;
  currencyLdFeatureFlag?: boolean;
}

const RemanPartsTable: FC<RemanPartsTableProps> = ({
  remanPartsList,
  priceAvailabilityData,
  locale,
  dealerData,
  dealerInformation,
  availRemoveInterimStockFlag,
  isShowSourcingDetailEnabled,
  isDeliveryEnabled,
  catcorp,
  isPccReleaseRemanReplacement,
  isRTL = false,
  currencyLdFeatureFlag = false
}) => {
  const t = useTranslations();
  const isMD = useBreakpoint(PAGE_BREAKPOINTS.MD);
  const { storeId, cartDetails, serialNumber } = useProductDetailPageContext();
  const { fireEvent: fireViewItemListEvent } = useViewItemListEvent();
  const { fireEvent: fireSelectItemEvent } = useSelectItemEvent();
  const [visibleParts, setVisibleParts] = useState(INITIAL_REMAN_PARTS);
  const isGAEventTriggered = useRef(false);
  const remanData = remanPartsList?.slice(0, visibleParts);
  const maxLength = remanPartsList?.slice(0, MAX_REMAN_PARTS);
  const loadMore = () => {
    setVisibleParts(visibleParts + INITIAL_REMAN_PARTS);
  };
  const isDesktop = useBreakpoint(PAGE_BREAKPOINTS.BLOCKS_LG);
  const { currency = '', priceAndAvailability } = priceAvailabilityData || {};
  const cartId =
    cartDetails !== null ? cartDetails?.orderHeader?.orderId || '' : null;

  const genItemsWithPrice = useCallback(
    (items, isForGAEvent = false) =>
      mergePriceAvailabilityResponse(
        items,
        priceAndAvailability,
        '',
        isForGAEvent
      ),
    [priceAndAvailability]
  );
  const remanPriceAndAvailability = genItemsWithPrice(remanData);
  const isEnableAddToCart = remanPriceAndAvailability.some(
    (item) => item.enableAddToCart
  );

  const gaItemsWithPrice = genItemsWithPrice(remanPartsList, true);

  const fireGAEvent = useCallback(() => {
    fireViewItemListEvent({
      cartId,
      itemListData: REMAN_VIEW_ITEM_LIST,
      locationId: storeId,
      products: gaItemsWithPrice as unknown as Product[] & ProductsPriceData[],
      isProductsData: true
    });
  }, [cartId, fireViewItemListEvent, gaItemsWithPrice, storeId]);

  useEffect(() => {
    if (!isGAEventTriggered.current && cartId !== null) {
      isGAEventTriggered.current = true;
      fireGAEvent();
    }
  }, [fireGAEvent, cartId]);

  const handleSelectItemGa = (remanPart: ProductsPriceData) => {
    fireSelectItemEvent({
      item: getFormattedSearchSelectItem({ orderItemId: cartId }, remanPart),
      partName: `${remanPart.partNumber}: ${remanPart.name}`,
      locationId: storeId,
      itemSummary: remanPart,
      isSearchPageEvent: true,
      itemListId: REMAN_SELECT_ITEM.item_list_id,
      itemListName: REMAN_SELECT_ITEM.item_list_name,
      serialNumber
    });
  };

  return (
    <>
      <div
        className={cx(
          { [styles['mobile-border']]: isPccReleaseRemanReplacement && isMD },
          'cat-u-margin-top-sm'
        )}
      >
        {isDesktop ? (
          <CatTableObject>
            <CatTable
              /* @ts-expect-error Modifying a var from blocks to fix the default high z-index of the table header cell */
              style={{ '--cat-table-header-cell-zindex-pass-down': '10' }}
            >
              <CatTableHeader className={styles['table-header']}>
                <CatTableRow>
                  <CatTableHeaderCell>
                    <span className="cat-u-theme-typography fw-bold">
                      {t('CAT_CONTACT_YOUR_DEALER_PRODUCT_NUMBER')}
                    </span>
                  </CatTableHeaderCell>
                  <CatTableHeaderCell>
                    {!catcorp && isEnableAddToCart && (
                      <span className="cat-u-theme-typography fw-bold">
                        {t('PDP_AVAILBLE_FOR_PICKUP')}
                      </span>
                    )}
                  </CatTableHeaderCell>
                  <CatTableHeaderCell align="right">
                    {!catcorp && isEnableAddToCart && (
                      <span className="cat-u-theme-typography fw-bold">
                        {t('TOTAL_PRICE')}
                      </span>
                    )}
                  </CatTableHeaderCell>
                  {!isEnableAddToCart && !catcorp && (
                    <CatTableHeaderCell
                      align="right"
                      width="200px"
                    ></CatTableHeaderCell>
                  )}
                  {isEnableAddToCart && !catcorp && (
                    <CatTableHeaderCell
                      align="right"
                      width="70px"
                    ></CatTableHeaderCell>
                  )}
                </CatTableRow>
              </CatTableHeader>
              <CatTableBody className={styles['table-body']}>
                {remanPriceAndAvailability?.map((item) => {
                  const { partNumber = '', name = '' } = item;
                  const [partWithPriceForGA] = mergePriceAvailabilityResponse(
                    [item],
                    priceAndAvailability,
                    '',
                    true
                  );
                  const enableAddToCart = item.enableAddToCart;
                  return (
                    <CatTableRow key={partNumber}>
                      <CatTableCell>
                        <CatTextLink
                          href={partNumber}
                          onClick={() => handleSelectItemGa(partWithPriceForGA)}
                          data-testid="table-body-row"
                        >
                          <PurifiedHtml
                            htmlString={`${partNumber} : ${name}`}
                          />
                        </CatTextLink>
                      </CatTableCell>
                      <CatTableCell>
                        {!catcorp && !!item && (
                          <CommonAvailabilityWrapper
                            availabilityData={item}
                            shouldDisplayLabel={false}
                            isLoading={false}
                            locale={locale}
                            dealerData={dealerData}
                            dealerInformation={dealerInformation}
                            availRemoveInterimStockFlag={
                              availRemoveInterimStockFlag
                            }
                            isShowSourcingDetailEnabled={
                              isShowSourcingDetailEnabled
                            }
                            isDeliveryEnabled={isDeliveryEnabled}
                          />
                        )}
                      </CatTableCell>
                      <CatTableCell align="right">
                        {!catcorp && !!item && (
                          <Price
                            price={item}
                            currency={currency}
                            isRTL={isRTL}
                            currencyLdFeatureFlag={currencyLdFeatureFlag}
                          />
                        )}
                      </CatTableCell>

                      {!catcorp && (
                        <Conditional
                          test={enableAddToCart}
                          fallback={
                            <CatTableCell>
                              <ContactYourDealerPopover
                                dealerAddress={dealerData?.address}
                                responsiveBehavior
                                fullWidth={false}
                              />
                            </CatTableCell>
                          }
                        >
                          <CatTableCell align="right" width="70px">
                            <AddToCart
                              productPrice={partWithPriceForGA}
                              productPriceData={partWithPriceForGA}
                              typeIcon={true}
                              cartId={cartId}
                              currency={currency}
                              itemlistIdName={PCC_PDP_REMAN_ADD_TO_CART}
                            />
                          </CatTableCell>
                        </Conditional>
                      )}
                    </CatTableRow>
                  );
                })}
              </CatTableBody>
            </CatTable>
          </CatTableObject>
        ) : (
          remanPriceAndAvailability?.map((remanItem) => {
            return (
              <PdpPartsMobileView
                key={remanItem.partNumber}
                remanItem={remanItem}
                enableAddToCart={remanItem.enableAddToCart}
                locale={locale}
                dealerData={dealerData}
                dealerInformation={dealerInformation}
                availRemoveInterimStockFlag={availRemoveInterimStockFlag}
                isShowSourcingDetailEnabled={isShowSourcingDetailEnabled}
                isDeliveryEnabled={isDeliveryEnabled}
                currency={currency}
                catcorp={catcorp}
                isPccReleaseRemanReplacement={isPccReleaseRemanReplacement}
                remanPart={true}
                isRTL={isRTL}
                currencyLdFeatureFlag={currencyLdFeatureFlag}
              />
            );
          })
        )}
      </div>
      {visibleParts < maxLength?.length && (
        <CatButton
          onClick={loadMore}
          className="cat-u-margin-top-md d-flex justify-content-center"
        >
          {t('LOAD_MORE')}
        </CatButton>
      )}
    </>
  );
};
export default RemanPartsTable;
